import { Marker } from 'maps/components/commons/Marker/Marker'
import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import { useMapDispatch } from 'maps/hooks/useMapDispatch/useMapDispatch'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import { geocode } from 'maps/services/client/geocode/geocode'
import type { Coordinates } from 'maps/types'
import { isSameCoordinates } from 'maps/utils/distance/distance'
import { MAP_STYLES_ID, ZOOM_MAP } from 'maps/utils/mapOptions/constants'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect, useRef, useState } from 'react'

interface GoogleMapsProps {
	readonly className?: string
	readonly children?: React.ReactNode
}

export function GoogleMaps({
	className,
	children,
}: GoogleMapsProps): React.ReactNode {
	const {
		country: { countryName, languageISO, countryISO },
	} = useMasterData()
	const { stores, filteredStores, searchInput } = useMapState()
	const dispatch = useMapDispatch()
	const { expanded, setExpanded } = useExpandedStore()
	const ref = useRef<HTMLDivElement | null>(null)
	const [map, setMap] = useState<google.maps.Map | undefined>(undefined)
	const [coordinates, setCoordinates] = useState<Coordinates | undefined>(
		expanded?.addresses.coordinates
			? {
					lat: expanded.addresses.coordinates.lat,
					lng: expanded.addresses.coordinates.lng,
				}
			: undefined
	)

	useEffect(() => {
		const getDefaultCoordinates = async () => {
			const location = await geocode({
				address: countryName,
				languageISO,
				countryISO,
			})
			if (!searchInput) {
				dispatch({ searchInput: countryName })
			}
			if (location) {
				setCoordinates(location.coordinates)
			}
		}

		if (map && !stores?.length && !coordinates) {
			getDefaultCoordinates()
		}
	}, [map, stores])

	useEffect(() => {
		if (ref.current && !map) {
			const handleClick = () => {
				// Deselect the expanded marker(store) and close its card
				if (expanded) {
					setExpanded()
				}
			}
			//Creates a new instance of the map with the options provided
			const mapInstance = new google.maps.Map(ref.current, {
				mapId: MAP_STYLES_ID,
				zoom: ZOOM_MAP,
				zoomControl: true,
				zoomControlOptions: {
					position: google.maps.ControlPosition.TOP_LEFT,
				},
				clickableIcons: false,
				disableDefaultUI: true, // Disable all the controls of the map (street view, map type...)
			})
			mapInstance.addListener('click', handleClick)
			setMap(mapInstance)
		}
	}, [ref.current, map])

	useEffect(() => {
		// Unbind map from all events on unmount
		return () => {
			if (map) {
				map.unbindAll()
				setMap(undefined)
			}
		}
	}, [map])

	useEffect(() => {
		const coordinatesExpanded = expanded?.addresses.coordinates
		if (!isSameCoordinates(coordinates, coordinatesExpanded)) {
			if (coordinatesExpanded) {
				const { lat, lng } = coordinatesExpanded
				setCoordinates({ lat, lng })
			} else {
				setCoordinates(undefined)
			}
		}
	}, [expanded])

	useEffect(() => {
		if (map && coordinates) {
			map.setZoom(ZOOM_MAP)
			map.panTo(coordinates)
		}
	}, [map, coordinates])

	return (
		<div className={className} ref={ref}>
			{map && (
				<>
					{filteredStores?.map((store) => (
						<Marker key={store.id} map={map} store={store} />
					))}
					{children}
				</>
			)}
		</div>
	)
}
